import * as PopoverPrimitive from '@radix-ui/react-popover'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale/es'
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useState } from 'react'
import {
  INICIALES_SEMANA,
  correrMes,
  diasDelMes,
  formatearMes,
  hoyEnBuenosAires,
} from '@/features/grilla/formato'
import { cn } from '@/lib/cn'
import { CONTROL } from './control'

interface SelectorFechaProps {
  /** El id que apunta la etiqueta del `Campo`. */
  id?: string
  /** "AAAA-MM-DD", o vacío. */
  valor: string
  alCambiar: (fecha: string) => void
  invalido?: boolean
  describedBy?: string | undefined
  /** Para campos optativos, como el período del proyecto: ofrece "Borrar". */
  opcional?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Selector de fecha del sistema (revisión del 21/09). Reemplaza al
 * `<input type="date">` del navegador, que dibuja cada navegador a su manera
 * y no respeta la tipografía, los colores ni el área táctil del resto de los
 * formularios.
 *
 * El control se ve como cualquier otro campo; al tocarlo, un calendario del
 * mes con la misma cuadrícula de lunes a domingo que la vista Calendario. La
 * fecha elegida queda en `data-valor` como "AAAA-MM-DD", que es lo que leen
 * las pruebas.
 */
export function SelectorFecha({
  id,
  valor,
  alCambiar,
  invalido,
  describedBy,
  opcional,
  disabled,
  className,
}: SelectorFechaProps) {
  const [abierto, setAbierto] = useState(false)
  const hoy = hoyEnBuenosAires()
  const [mes, setMes] = useState(() => (valor || hoy).slice(0, 7))

  const elegir = (fecha: string) => {
    alCambiar(fecha)
    setAbierto(false)
  }

  return (
    <PopoverPrimitive.Root
      open={abierto}
      onOpenChange={(o) => {
        // Al abrir, el calendario arranca en el mes de la fecha elegida.
        if (o) setMes((valor || hoy).slice(0, 7))
        setAbierto(o)
      }}
    >
      <PopoverPrimitive.Trigger
        id={id}
        type="button"
        disabled={disabled}
        aria-invalid={invalido}
        aria-describedby={describedBy}
        data-valor={valor}
        className={cn(CONTROL, 'flex min-h-11 items-center gap-2 text-left', className)}
      >
        <span className={cn('min-w-0 flex-1 truncate', !valor && 'text-tinta-suave/70')}>
          {valor
            ? format(parseISO(valor), "d 'de' MMM yyyy", { locale: es })
            : 'Elegí una fecha'}
        </span>
        <CalendarIcon className="size-4 shrink-0 text-tinta-suave" aria-hidden />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-[60] w-[19rem] rounded-2xl border border-borde bg-tarjeta p-3 shadow-tarjeta"
          aria-label="Calendario"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Mes anterior"
              onClick={() => {
                setMes(correrMes(mes, -1))
              }}
              className="flex size-9 items-center justify-center rounded-full hover:bg-lienzo"
            >
              <ChevronLeftIcon className="size-5" aria-hidden />
            </button>
            <span data-mes-selector className="text-sm font-medium">
              {formatearMes(mes)}
            </span>
            <button
              type="button"
              aria-label="Mes siguiente"
              onClick={() => {
                setMes(correrMes(mes, 1))
              }}
              className="flex size-9 items-center justify-center rounded-full hover:bg-lienzo"
            >
              <ChevronRightIcon className="size-5" aria-hidden />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center">
            {INICIALES_SEMANA.map((d) => (
              <span
                key={d}
                className="py-1 text-xs font-medium text-tinta-suave"
                aria-hidden
              >
                {d}
              </span>
            ))}
            {diasDelMes(mes).map((dia) => {
              const deOtroMes = !dia.startsWith(mes)
              const elegido = dia === valor
              return (
                <button
                  key={dia}
                  type="button"
                  data-fecha={dia}
                  aria-label={format(parseISO(dia), "EEEE d 'de' MMMM 'de' yyyy", {
                    locale: es,
                  })}
                  aria-pressed={elegido}
                  onClick={() => {
                    elegir(dia)
                  }}
                  className={cn(
                    'flex size-9 items-center justify-center justify-self-center rounded-full text-sm',
                    elegido
                      ? 'bg-tinta font-semibold text-white'
                      : dia === hoy
                        ? 'font-semibold text-hoy ring-1 ring-hoy'
                        : 'hover:bg-lienzo',
                    deOtroMes && !elegido && 'text-tinta-suave/50',
                  )}
                >
                  {Number(dia.slice(8))}
                </button>
              )
            })}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-borde pt-2">
            <button
              type="button"
              onClick={() => {
                elegir(hoy)
              }}
              className="rounded-full px-3 py-1.5 text-sm font-medium hover:bg-lienzo"
            >
              Hoy
            </button>
            {opcional && valor && (
              <button
                type="button"
                onClick={() => {
                  elegir('')
                }}
                className="rounded-full px-3 py-1.5 text-sm text-tinta-suave hover:bg-lienzo hover:text-tinta"
              >
                Borrar
              </button>
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
